import { api } from "../api";

export interface OnboardingState {
  onboardingCompletedAt: string | null;
  onboardingSteps: string[];
  classCount: number;
  studentCount: number;
  practiceCount: number;
}

interface Step {
  id: string;
  label: string;
  description: string;
  nav: string;
  navLabel: string;
  manual: boolean;
}

const STEPS: Step[] = [
  { id: "departments", label: "Fachbereiche einrichten", description: "Wähle oder konfiguriere die Fachbereiche für deine Ausbildungsform", nav: "einstellungen", navLabel: "Einstellungen", manual: true },
  { id: "classes", label: "Erste Klasse anlegen", description: "Erstelle mindestens eine Klasse", nav: "klassen", navLabel: "Klassen", manual: false },
  { id: "students", label: "Schüler hinzufügen", description: "Importiere oder lege Schüler manuell an", nav: "schueler", navLabel: "Schüler", manual: false },
  { id: "practices", label: "Praktikumsstellen einrichten", description: "Erfasse deine Kooperationspartner", nav: "praktikumsstellen", navLabel: "Praktikumsstellen", manual: false },
  { id: "modules", label: "Module entdecken", description: "Planung & Schüler-Portal freischalten", nav: "abonnements", navLabel: "Entdecken", manual: true },
];

function isStepDone(step: Step, state: OnboardingState): boolean {
  if (step.manual) return state.onboardingSteps.includes(step.id);
  switch (step.id) {
    case "classes": return state.classCount > 0;
    case "students": return state.studentCount > 0;
    case "practices": return state.practiceCount > 0;
    default: return false;
  }
}

export function shouldShowOnboarding(state: OnboardingState): boolean {
  return !state.onboardingCompletedAt;
}

export function renderOnboardingChecklist(state: OnboardingState): string {
  const doneCount = STEPS.filter(s => isStepDone(s, state)).length;
  const total = STEPS.length;
  const pct = Math.round((doneCount / total) * 100);

  // Breitester Button bestimmt die Mindestbreite für alle
  const maxLabel = STEPS.reduce((a, s) => s.navLabel.length > a.length ? s.navLabel : a, "");
  const btnStyle = `flex-shrink:0;min-width:${maxLabel.length + 2}ch;text-align:center`;

  const items = STEPS.map(step => {
    const done = isStepDone(step, state);
    if (done) {
      return `<div style="display:flex;align-items:center;gap:.75rem;padding:.625rem 0;opacity:.7">
        <span style="color:var(--green-500);font-size:1.125rem;flex-shrink:0;width:1.5rem;text-align:center">✓</span>
        <div style="flex:1;min-width:0">
          <div style="font-weight:500;text-decoration:line-through;color:var(--gray-500)">${step.label}</div>
        </div>
        <a data-nav="${step.nav}" class="btn btn-sm btn-secondary" style="${btnStyle}">${step.navLabel}</a>
      </div>`;
    }
    const circleHtml = step.manual
      ? `<span class="onboarding-circle" data-step="${step.id}" style="color:var(--gray-300);font-size:1.125rem;flex-shrink:0;width:1.5rem;text-align:center;cursor:pointer;user-select:none;transition:color .15s" title="Als erledigt markieren">○</span>`
      : `<span style="color:var(--gray-300);font-size:1.125rem;flex-shrink:0;width:1.5rem;text-align:center">○</span>`;
    return `<div style="display:flex;align-items:center;gap:.75rem;padding:.625rem 0">
      ${circleHtml}
      <div style="flex:1;min-width:0">
        <div style="font-weight:500">${step.label}</div>
        <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.125rem">${step.description}</div>
      </div>
      <a data-nav="${step.nav}" class="btn btn-sm btn-primary" style="${btnStyle}">${step.navLabel} →</a>
    </div>`;
  }).join('<div style="border-top:1px solid var(--gray-200)"></div>');

  return `<div style="border:2px solid var(--primary);border-radius:.5rem;padding:1.25rem;margin-bottom:1.25rem;background:linear-gradient(135deg,#f0f7ff 0%,#fff 100%)">
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:.75rem">
      <h2 style="font-size:1.125rem;font-weight:600;margin:0">Einrichtung abschließen</h2>
      <span style="font-size:.8125rem;color:var(--gray-500)">${doneCount} von ${total} erledigt</span>
    </div>
    <div style="height:6px;background:var(--gray-200);border-radius:3px;margin-bottom:1rem;overflow:hidden">
      <div style="height:100%;width:${pct}%;background:var(--primary);border-radius:3px;transition:width .3s ease"></div>
    </div>
    ${items}
    <div style="text-align:center;margin-top:.75rem">
      <a id="onboarding-dismiss" style="font-size:.8125rem;color:var(--gray-400);cursor:pointer;text-decoration:underline">Nicht mehr anzeigen</a>
    </div>
  </div>`;
}

export function attachChecklistListeners(el: HTMLElement, onRefresh: () => void): void {
  // Clickable circles for manual steps
  el.querySelectorAll<HTMLElement>(".onboarding-circle").forEach(circle => {
    circle.addEventListener("mouseenter", () => { circle.style.color = "var(--primary)"; circle.textContent = "●"; });
    circle.addEventListener("mouseleave", () => { circle.style.color = "var(--gray-300)"; circle.textContent = "○"; });
    circle.addEventListener("click", async () => {
      const stepId = circle.dataset.step;
      if (!stepId) return;
      circle.style.pointerEvents = "none";
      circle.style.color = "var(--green-500)";
      circle.textContent = "✓";
      try {
        await api.settings.completeOnboardingStep(stepId);
        onRefresh();
      } catch {
        circle.style.pointerEvents = "";
        circle.style.color = "var(--gray-300)";
        circle.textContent = "○";
      }
    });
  });

  // Dismiss
  const dismiss = el.querySelector("#onboarding-dismiss");
  dismiss?.addEventListener("click", async () => {
    try {
      await api.settings.completeOnboarding();
      onRefresh();
    } catch {}
  });
}
